import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./AdminControl.css";
import {
  FaPause,
  FaHourglassHalf,
  FaCheckCircle,
  FaTrash,
  FaSyncAlt,
  FaSearch,
  FaTools,
  FaArrowRight,
  FaArrowLeft,
  FaHistory,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const API_URL = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;

export default function AdminControl() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("PENDING");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusLoading, setStatusLoading] = useState(null);

  // --- 1. FETCH ALL DATA ---
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      setTransactions(data.transactions || []);
    } catch (err) {
      console.error("Fetch Error:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // --- 2. MASTER REBUILD LOGIC ---

  // --- 3. DELETE/REJECT TRANSACTION ---
  const handleAdminDelete = async (tx) => {
    if (
      !window.confirm(
        "MASTER DELETE: This will REJECT this record and its LINKED record, then recalculate balances for all affected branches. Continue?",
      )
    )
      return;

    setLoading(true);
    try {
      // 1. REJECT the Primary Record
      await fetch(API_URL, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentId: tx.paymentId,
          paymentType: tx.paymentType,
          status: "REJECTED",
          remarks: "REJECTED BY MASTER ADMIN (Primary)",
        }),
      });

      // 2. REJECT the Linked Record (if it exists)
      if (tx.linkedId) {
        // Determine the mirror payment type
        let oppositeType;
        const type = tx.paymentType;

        if (type.includes("BTSPLIT"))
          oppositeType = type.includes("IN") ? "BTSPLITOUT" : "BTSPLITIN";
        else if (type.includes("SPLIT"))
          oppositeType = type.includes("IN") ? "SPLITOUT" : "SPLITIN";
        else if (type.startsWith("BT"))
          oppositeType = type.includes("IN") ? "BTOUT" : "BTIN";
        else oppositeType = type === "IN" ? "OUT" : "IN";

        await fetch(API_URL, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paymentId: tx.linkedId,
            paymentType: oppositeType,
            status: "REJECTED",
            remarks: `REJECTED BY MASTER ADMIN (Linked to ${tx.paymentId})`,
          }),
        });
      }

      // 3. START REBUILDS
      // Rebuild for the Sender Branch

      alert(
        "Master Delete successful: Both records rejected and balances audited.",
      );
      fetchAllData(); // Refresh the main table
    } catch (err) {
      console.error("Master Delete Error:", err);
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- 4. UPDATE STATUS ---
  const updateStatus = async (tx, newStatus) => {
    if (!window.confirm(`Change status to "${newStatus}" for ${tx.paymentId}?`)) return;
    setStatusLoading(tx.paymentId + newStatus);
    try {
      const res = await fetch(API_URL, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentId: tx.paymentId,
          paymentType: tx.paymentType,
          status: newStatus,
          linkedId: tx.linkedId || null,
          linkedPaymentType: tx.linkedPaymentType || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Status update failed.");
      }
      setTransactions((prev) =>
        prev.map((t) =>
          t.paymentId === tx.paymentId || t.paymentId === tx.linkedId
            ? { ...t, status: newStatus }
            : t,
        ),
      );
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setStatusLoading(null);
    }
  };

  // --- 5. FILTERING & SPLITTING LOGIC ---
  const { senders, receivers } = useMemo(() => {
    const filtered = transactions
      .filter((tx) => {
        const isCorrectStatus = tx.status === activeTab;
        const matchesSearch = (tx.fromBranch + tx.toBranch + tx.paymentId)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        return isCorrectStatus && matchesSearch;
      })
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return {
      senders: filtered.filter((tx) => tx.flowRole === "SENDER"),
      receivers: filtered.filter((tx) => tx.flowRole === "RECEIVER"),
    };
  }, [transactions, activeTab, searchTerm]);

  // Helper for Table Column
  const ALL_STATUSES = ["PENDING", "APPROVED", "WAITING", "DELIVERED", "REJECTED"];

  const RenderTable = ({ data, title, icon, colorClass }) => (
    <div className={`admin-column ${colorClass}`}>
      <div className="column-header">
        {icon}{" "}
        <span>
          {title} ({data.length})
        </span>
      </div>
      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>D/T</th>
              <th>TYPE</th>
              <th>ROUTE</th>
              <th>AMOUNT</th>
              <th>REMARKS</th>
              <th>PARTY</th>
              <th>ACTION</th>
              <th>ID</th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((tx) => (
                <tr key={tx.paymentId}>
                  <td>
                    <div className="id-stack">
                      <span className="tx-id-short">
                        {new Date(tx.timestamp).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </span>
                      <small className="tx-time-short">
                        {new Date(tx.timestamp).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </small>
                    </div>
                  </td>
                  <td>
                    <span className={`badge-mini ${tx.paymentType}`}>
                      {tx.paymentType}
                    </span>
                  </td>
                  <td>
                    <div className="route-text">
                      <strong>{tx.fromBranch}</strong> →{" "}
                      <strong>{tx.toBranch}</strong>
                    </div>
                  </td>
                  <td className="amt-bold">
                    ₹{Number(tx.amount).toLocaleString()}
                  </td>
                  <td>
                    <small>{tx.remarks}</small>
                  </td>
                  <td>
                    <span className="tx-id-short">{tx.receiverName}</span>
                    <br />
                    <small className="tx-time-short">{tx.receiverCell}</small>
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <select
                        value={tx.status}
                        disabled={!!statusLoading}
                        onChange={(e) => updateStatus(tx, e.target.value)}
                        style={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          padding: "4px 6px",
                          borderRadius: "6px",
                          border: "1.5px solid #cbd5e1",
                          background:
                            tx.status === "APPROVED" ? "#dcfce7" :
                            tx.status === "DELIVERED" ? "#dbeafe" :
                            tx.status === "WAITING" ? "#fef9c3" :
                            tx.status === "REJECTED" ? "#fee2e2" :
                            "#f1f5f9",
                          cursor: "pointer",
                          outline: "none",
                        }}
                      >
                        {ALL_STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {statusLoading && statusLoading.startsWith(tx.paymentId) && (
                        <FaSyncAlt className="spin" style={{ fontSize: "0.75rem", color: "#64748b" }} />
                      )}
                      <button
                        onClick={() => handleAdminDelete(tx)}
                        className="btn-trash"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className="tx-id-short">{tx.paymentId}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="empty-msg">
                  No records
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="admin-control-container">
      <header className="admin-header">
        <div className="brand-stack">
          <h1 style={{ color: "white" }}>
            <FaTools /> Master Control
          </h1>
        </div>

        <div className="header-actions">
          <Link to="/">
            <button className="Branch-nav">Back to Dashboard</button>
          </Link>
          <div className="search-pill">
            <FaSearch />
            <input
              placeholder="Search ID or Branch..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button onClick={fetchAllData} className="btn-refresh">
            <FaSyncAlt className={loading ? "spin" : ""} /> Sync
          </button>
        </div>
      </header>

      <nav className="admin-tab-bar">
        <button
          className={activeTab === "PENDING" ? "tab active" : "tab"}
          onClick={() => setActiveTab("PENDING")}
        >
          <FaPause /> Pending
        </button>
        <button
          className={activeTab === "APPROVED" ? "tab active" : "tab"}
          onClick={() => setActiveTab("APPROVED")}
        >
          <FaCheckCircle /> Approved
        </button>
        <button
          className={activeTab === "WAITING" ? "tab active" : "tab"}
          onClick={() => setActiveTab("WAITING")}
        >
          <FaHourglassHalf /> Waiting
        </button>
      </nav>

      <main className="split-view-body">
        {loading ? (
          <div className="loading-state">
            <FaHistory className="spin" /> Syncing with Ledger...
          </div>
        ) : (
          <div className="columns-container">
            <RenderTable
              data={senders}
              title="Senders"
              icon={<FaArrowRight />}
              colorClass="sender-view"
            />
            <div className="divider-line"></div>
            <RenderTable
              data={receivers}
              title="Receivers"
              icon={<FaArrowLeft />}
              colorClass="receiver-view"
            />
          </div>
        )}
      </main>
    </div>
  );
}
